import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FiCamera, FiUpload, FiTrash2, FiUser } from 'react-icons/fi';
import { collection, getDocs, addDoc, deleteDoc, doc } from 'firebase/firestore';
import { db } from '../../firebase/firebase';

const GalleryManager = () => {
  const [artists, setArtists] = useState([]);
  const [selectedArtist, setSelectedArtist] = useState('All');
  const [galleryImages, setGalleryImages] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      // Load artists
      const artistsSnapshot = await getDocs(collection(db, 'artists'));
      const artistsData = artistsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setArtists(artistsData);

      // Load gallery images
      const gallerySnapshot = await getDocs(collection(db, 'gallery'));
      const galleryData = gallerySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setGalleryImages(galleryData);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Function to compress image to stay under 1MB
  const compressImage = (file) => {
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement('canvas');
          let width = img.width;
          let height = img.height;
          
          // Target max dimension of 1200px (good quality for web)
          const maxDimension = 1200;
          
          if (width > height && width > maxDimension) {
            height = (height * maxDimension) / width;
            width = maxDimension;
          } else if (height > maxDimension) {
            width = (width * maxDimension) / height;
            height = maxDimension;
          }
          
          canvas.width = width;
          canvas.height = height;
          
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, width, height);
          
          // Start with quality 0.8, reduce if needed
          let quality = 0.8;
          let compressedDataUrl = canvas.toDataURL('image/jpeg', quality);
          
          // If still too large, reduce quality further
          while (compressedDataUrl.length > 900000 && quality > 0.3) {
            quality -= 0.1;
            compressedDataUrl = canvas.toDataURL('image/jpeg', quality);
          }
          
          resolve({
            dataUrl: compressedDataUrl,
            width: width,
            height: height
          });
        };
        img.src = e.target.result;
      };
      reader.readAsDataURL(file);
    });
  };

  const handleImageUpload = async (e) => {
    const files = Array.from(e.target.files);
    
    if (selectedArtist === 'All') {
      alert('Please select an artist before uploading images');
      return;
    }
    
    setUploading(true);

    try {
      for (const file of files) {
        const compressed = await compressImage(file);
        
        const imageData = {
          original: compressed.dataUrl,
          width: compressed.width,
          height: compressed.height,
          artist: selectedArtist,
          uploadedAt: new Date().toISOString()
        };
        
        await addDoc(collection(db, 'gallery'), imageData);
      }
      
      await loadData();
      alert('Images uploaded successfully!');
    } catch (error) {
      console.error('Error uploading images:', error);
      alert('Failed to upload images. Please try smaller images or fewer at once.');
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteImage = async (id) => {
    if (window.confirm('Are you sure you want to delete this image?')) {
      try {
        await deleteDoc(doc(db, 'gallery', id));
        await loadData();
      } catch (error) {
        console.error('Error deleting image:', error);
        alert('Failed to delete image. Please try again.');
      }
    }
  };

  const filteredImages = selectedArtist === 'All' 
    ? galleryImages 
    : galleryImages.filter(img => img.artist === selectedArtist);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-white">Loading gallery...</div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center gap-2 sm:gap-3 mb-4 sm:mb-6">
        <FiCamera className="text-2xl sm:text-3xl text-white" />
        <h2 className="text-xl sm:text-2xl font-bold text-white">Gallery Management</h2>
      </div>

      {/* Artist Filter */}
      <div className="mb-4 sm:mb-6">
        <label className="block text-white/80 mb-3 text-xs sm:text-sm font-medium flex items-center gap-2">
          <FiUser />
          Filter by Artist
        </label>
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => setSelectedArtist('All')}
            className={`px-3 sm:px-4 py-2 rounded-lg font-medium transition-all text-sm sm:text-base ${
              selectedArtist === 'All'
                ? 'bg-white text-slate-900'
                : 'bg-white/10 text-white hover:bg-white/20'
            }`}
          >
            All Artists
          </button>
          {artists.map((artist) => (
            <button
              key={artist.id}
              onClick={() => setSelectedArtist(artist.name)}
              className={`px-3 sm:px-4 py-2 rounded-lg font-medium transition-all text-sm sm:text-base ${
                selectedArtist === artist.name
                  ? 'bg-white text-slate-900'
                  : 'bg-white/10 text-white hover:bg-white/20'
              }`}
            >
              {artist.name}
            </button>
          ))}
        </div>
      </div>

      {/* Upload Section */}
      <div className="mb-4 sm:mb-6 bg-white/5 rounded-xl p-4 sm:p-6 border border-white/10">
        <label className="block cursor-pointer">
          <div className="border-2 border-dashed border-white/20 rounded-xl p-6 sm:p-8 text-center hover:border-white/40 transition-all bg-white/5">
            <FiUpload className="mx-auto text-3xl sm:text-4xl text-white/60 mb-2 sm:mb-3" />
            <p className="text-white/80 mb-2 font-medium text-sm sm:text-base">
              {uploading ? 'Uploading and compressing...' : 'Click to upload images'}
            </p>
            <p className="text-white/40 text-xs sm:text-sm">
              {selectedArtist === 'All' 
                ? 'Select an artist filter to assign images' 
                : `Upload images for ${selectedArtist}`}
            </p>
            <p className="text-white/40 text-xs mt-2">
              Images will be compressed for optimal web performance
            </p>
            <input
              type="file"
              accept="image/*"
              multiple
              onChange={handleImageUpload}
              className="hidden"
              disabled={uploading || selectedArtist === 'All'}
            />
          </div>
        </label>
      </div>

      {/* Gallery Grid */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-base sm:text-lg font-semibold text-white">
            {filteredImages.length} {filteredImages.length === 1 ? 'Image' : 'Images'}
            {selectedArtist !== 'All' && ` by ${selectedArtist}`}
          </h3>
        </div>

        {filteredImages.length > 0 ? (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4">
            <AnimatePresence>
              {filteredImages.map((image) => (
                <motion.div
                  key={image.id}
                  layout
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.8 }}
                  className="relative group bg-white/5 rounded-lg overflow-hidden border border-white/10"
                >
                  <img
                    src={image.original}
                    alt="Gallery"
                    className="w-full h-40 sm:h-48 object-cover"
                  />
                  
                  {/* Overlay */}
                  <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col items-center justify-center gap-2">
                    <div className="text-white/80 text-xs sm:text-sm mb-2">
                      {image.artist}
                    </div>
                    <button
                      onClick={() => handleDeleteImage(image.id)}
                      className="flex items-center gap-2 px-3 sm:px-4 py-2 bg-red-500 hover:bg-red-600 text-white rounded-lg transition-all text-xs sm:text-sm"
                    >
                      <FiTrash2 />
                      <span>Delete</span>
                    </button>
                  </div>
                </motion.div>
              ))}
            </AnimatePresence>
          </div>
        ) : (
          <div className="text-center py-8 sm:py-12 bg-white/5 rounded-xl border border-white/10">
            <FiCamera className="mx-auto text-4xl sm:text-5xl text-white/40 mb-3 sm:mb-4" />
            <p className="text-white/60 text-sm sm:text-base">
              {selectedArtist === 'All' 
                ? 'No images in gallery yet' 
                : `No images for ${selectedArtist} yet`}
            </p>
            <p className="text-white/40 text-xs sm:text-sm mt-2">
              Select an artist and upload images to get started
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default GalleryManager;